import { useCallback, useContext, useEffect, useState } from "react";
import Loader from "../../components/Loader";
import { Link, useNavigate } from "react-router-dom";
import { Box, Grid2, Tab } from "@mui/material";
import StyledGridComp from "../../components/StyledGridComp";
import { formatDateWithoutTime, notifyError } from "../../utilities/helper";
import { MenuContext } from "../../App";
import SearchedComponent from "../../components/SearchedComponent";
import { TabContext, TabList, TabPanel } from "@mui/lab";
import {
  checkAcknowledgmentTypeService,
  fetchAllDrugOrderDataService,
  fetchAllSiteDrugOrders,
} from "../../services/drugOrderManagementService";
import useDebounceSearch from "../../hooks/useDebounceSearch";

const DrugOrderManagement = () => {
  const [load, setLoad] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(15);
  const [rowData, setRowData] = useState([]);
  const [rowLength, setRowLength] = useState(0);
  const [canAddDrugOrder, setCanAddDrugOrder] = useState(false);
  const [searchedValue, setSearchedValue] = useState("");
  const [tabValue, setTabValue] = useState("site");
  const [isDetailedAcknowledge, setIsDetailAcknowledge] = useState(false);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  const navigate = useNavigate();

  const access_token = localStorage?.getItem("access_token");

  const role = localStorage.getItem("role");
  const roleId = localStorage.getItem("roleId");
  const { systemActions } = useContext(MenuContext);

  const debounceQuery = useDebounceSearch(searchedValue, 500);

  useEffect(() => {
    if (page >= 0 && pageSize && access_token) {
      getAllDrugOrderManagementData(
        roleId,
        pageSize,
        page,
        access_token,
        debounceQuery,
        tabValue
      );
    }
  }, [page, pageSize, access_token, debounceQuery, tabValue]);

  useEffect(() => {
    if (tabValue === "site") {
      getAcknowledgmentType();
    } else {
      setIsDetailAcknowledge(false);
    }
  }, [tabValue]);

  useEffect(() => {
    if (systemActions?.length > 0 && role !== "Admin") {
      const found = systemActions?.find(
        (item) => item?.name === "CanCreateDepotList"
      );
      if (found) {
        setCanAddDrugOrder(true);
      } else {
        setCanAddDrugOrder(false);
      }
    } else if (role === "Admin") {
      setCanAddDrugOrder(true);
    }
  }, [systemActions, role]);

  const getAllDrugOrderManagementData = async (
    roleId,
    pageSize,
    page,
    access_token,
    debouncedSearch = "",
    tabValue
  ) => {
    setLoad(true);
    try {
      const enpoint =
        tabValue === "site"
          ? fetchAllSiteDrugOrders
          : fetchAllDrugOrderDataService;

      const res = await enpoint(
        page + 1,
        pageSize,
        access_token,
        debouncedSearch,
        roleId
      );
      if (res?.status == 200) {
        setLoad(false);
        setRowData(
          res?.data?.result?.map((item) => ({
            ...item,
            id: item.drugOrderId,
            sourceDepotName: item?.source?.depotName,
            destination: item?.destination?.depotId
              ? item?.destination?.depotName
              : `Site ${item?.destination?.siteNumber}`,
          }))
        );
        setRowLength(res?.data?.totalCount);
      }
    } catch (err) {
      setLoad(false);
      notifyError(err?.mesage);
      console.log(err);
      setRowData([]);
      setRowLength(0);
      if (err?.status == 401) {
        navigate("/unauthorize");
      }
    }
  };

  const getAcknowledgmentType = async () => {
    try {
      const res = await checkAcknowledgmentTypeService(access_token);
      if (res?.data?.status == 200) {
        if (res?.data?.result === "Detailed") {
          setIsDetailAcknowledge(true);
        } else {
          setIsDetailAcknowledge(false);
        }
      }
    } catch (err) {
      console.log("Error:", err?.mesage);
      if (err?.status == 401) {
        navigate("unAuthorize");
      }
    }
  };

  const columns = [
    {
      field: "drugOrderNumber",
      headerName: "Order Number",
      width: 150,
      renderHeader: () => <div className="grid-heading">{"Order Number"}</div>,
      renderCell: (params) => {
        const drugOrderNumber = params.row.drugOrderNumber;

        return <span className="data-font">{drugOrderNumber}</span>;
      },
    },
    {
      field: "sourceDepotName",
      headerName: "Source",
      width: 200,
      renderHeader: () => <div className="grid-heading">{"Source"}</div>,
      renderCell: (params) => {
        const source = params.row.sourceDepotName;

        return <span className="data-font">{source}</span>;
      },
    },
    {
      field: "destination",
      headerName: "Destination",
      width: 200,
      renderHeader: () => <div className="grid-heading">{"Destination"}</div>,
      renderCell: (params) => {
        const destination = params.row.destination;

        return <span className="data-font">{destination}</span>;
      },
    },
    {
      field: "dateOrderRaised",
      headerName: "Date Order Raised",
      width: 150,
      renderHeader: () => (
        <div className="grid-heading">{"Date Order Raised"}</div>
      ),
      renderCell: (params) => {
        const dateOrderRaised = params.row.dateOrderRaised;

        return <span className="data-font">{dateOrderRaised}</span>;
      },
    },
    {
      field: "dateOrderAcknowledge",
      headerName: "Date Order Acknowledge",
      width: 200,
      renderHeader: () => (
        <div className="grid-heading">{"Date Order Acknowledge"}</div>
      ),
      renderCell: (params) => {
        const dateOrderAcknowledge = params.row.dateOrderAcknowledge;
        return (
          <span className="data-font">
            {dateOrderAcknowledge
              ? formatDateWithoutTime(dateOrderAcknowledge)
              : "N/A"}
          </span>
        );
      },
    },
    {
      field: "acknowledge",
      headerName: "Acknowledge",
      filterable: false,
      width: 150,
      renderHeader: () => <div className="grid-heading">{"Acknowledge"}</div>,
      renderCell: (params) => {
        const acknowledge =
          params.row.orderStatusDescription === "Acknowledged" ? true : false;
        const isDisabled =
          params.row.orderStatusDescription === "Acknowledged" ||
          params.row.orderStatusDescription === "Cancelled"
            ? true
            : false;
        const id = params.row.id;

        const url = isDetailedAcknowledge
          ? "acknowledge-detailed-drug-order"
          : "acknowledge-drug-order";

        return (
          <>
            {acknowledge ? (
              <span className="data-font">Acknowledged</span>
            ) : (
              <button
                className="grid-btn"
                disabled={isDisabled}
                style={isDisabled ? { cursor: "auto", opacity: 0.5 } : {}}
                onClick={() => {
                  navigate(`/${url}`, {
                    state: {
                      id: id,
                    },
                  });
                }}
              >
                Acknowledge
              </button>
            )}
          </>
        );
      },
    },
    {
      field: "cancel",
      headerName: "Cancel",
      filterable: false,
      width: 150,
      renderHeader: () => <div className="grid-heading">{"Cancel"}</div>,
      renderCell: (params) => {
        const cancel =
          params.row.orderStatusDescription === "Cancelled" ? true : false;
        const isDisabled =
          params.row.orderStatusDescription === "Acknowledged" ||
          params.row.orderStatusDescription === "Cancelled"
            ? true
            : false;
        const id = params?.row?.id;

        return (
          <>
            {!cancel ? (
              <button
                className="grid-btn-outlined"
                disabled={isDisabled}
                style={isDisabled ? { cursor: "auto", opacity: 0.5 } : {}}
                onClick={() =>
                  navigate("/cancel-drug-order", {
                    state: {
                      id: id,
                    },
                  })
                }
              >
                Cancel Order
              </button>
            ) : (
              <span className="data-font">Cancelled</span>
            )}
          </>
        );
      },
    },
    {
      field: "quarantine",
      headerName: "Quarantine",
      filterable: false,
      width: 150,
      renderHeader: () => <div className="grid-heading">{"Quarantine"}</div>,
      renderCell: (params) => {
        const quarantine =
          params.row.hasQuarantinedDrugs &&
          params.row.orderStatusDescription === "Acknowledged";

        const id = params?.row?.id;

        return (
          <>
            {quarantine && (
              <button
                className="grid-btn"
                onClick={() =>
                  navigate("/quarantine-drug-order", {
                    state: {
                      id: id,
                    },
                  })
                }
              >
                Process Quarantine
              </button>
            )}
          </>
        );
      },
    },
  ];

  const handleSearch = useCallback((e) => {
    setSearchedValue(e.target.value);
  }, []);

  return (
    <div className="content-body">
      <p className="bread-crumbs-link-container" style={{ fontWeight: "600" }}>
        <Link className="bread-crumbs-link" to="/">
          Home
        </Link>{" "}
        |{" "}
        <Link className="bread-crumbs-link" to="/drug-shipment-management">
          Drug Order
        </Link>
        -{" "}
        <b style={{ color: "gray" }}>{localStorage?.getItem("studyName")}</b>
      </p>

      <Grid2
        container
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: "100%", margin: "0 auto" }}
      >
        <Grid2 item md={4}>
          <p className="page-heading">Drug Order Management</p>
        </Grid2>
        <Grid2 item md={8}>
          <div className="search-bar-container">
            <SearchedComponent
              searchedValue={searchedValue}
              handleSearch={handleSearch}
              placeholder={"Drug Order"}
            />
            {canAddDrugOrder ? (
              <button
                className="create-btn"
                onClick={() => navigate("/addUpdate-drug-order")}
              >
                Add New Drug Order
              </button>
            ) : null}
          </div>
        </Grid2>
      </Grid2>

      {/* Tab Content */}

      <Box sx={{ width: "100%", typography: "body1" }}>
        <TabContext value={tabValue}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <TabList
              onChange={handleTabChange}
              aria-label="lab API tabs example"
              TabIndicatorProps={{ style: { display: "none" } }}
              sx={{
                "& .MuiTab-root": {
                  textTransform: "none",
                  wordSpacing: "-2px",
                  fontWeight: 500,
                  borderRadius: "999px",
                  minHeight: "40px",
                  px: 3,
                  py: 1,
                  mx: 0.5,
                  transition: "all 0.3s ease",
                  color: "text.secondary",
                  bgcolor: "transparent",
                },
                "& .Mui-selected": {
                  bgcolor: "primary.main",
                  color: "white !important",
                  boxShadow: 2,
                },
              }}
            >
              <Tab label="Site Orders" value="site" />
              <Tab label="Depot Orders" value="depot" />
            </TabList>
          </Box>
          <TabPanel value="site" sx={{ padding: "30px 0px" }}>
            {load ? (
              <Loader />
            ) : (
              <StyledGridComp
                rows={rowData}
                columns={columns}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                length={rowLength}
              ></StyledGridComp>
            )}
          </TabPanel>
          <TabPanel value="depot" sx={{ padding: "30px 0px" }}>
            {load ? (
              <Loader />
            ) : (
              <StyledGridComp
                rows={rowData}
                columns={columns}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                length={rowLength}
              ></StyledGridComp>
            )}
          </TabPanel>
          <TabPanel value="all" sx={{ padding: "30px 0px" }}>
            {load ? (
              <Loader />
            ) : (
              <StyledGridComp
                rows={rowData}
                columns={columns}
                page={page}
                setPage={setPage}
                pageSize={pageSize}
                setPageSize={setPageSize}
                length={rowLength}
              ></StyledGridComp>
            )}
          </TabPanel>
        </TabContext>
      </Box>

      <Grid2
        alignItems={"end"}
        justifyContent="end"
        container
        sx={{ marginTop: "2%" }}
      >
        <Grid2 item md={6}>
          <button className="create-btn">Back</button>
        </Grid2>
      </Grid2>
    </div>
  );
};

export default DrugOrderManagement;
